import { compose } from '@bem-react/core';
import { Registry, withRegistry } from '@bem-react/di';
import b from 'b_';
import React, { memo, RefObject, useRef, useState } from 'react';

import { ImageMenuItem } from '../../typings';
import { SCREEN_RESOLUTIONS } from '../image/constants';
import { ImageContextProvider } from '../image/context';
import Image from '../image/image';
import { withPopupListStyle } from '../popup-list/PopupList';
import { cnMenu, MenuBase, MixedItem } from '../ui/Menu/Menu';

import { ImagesPopup, OnImagesPopupClose, ImagesPopupDirection } from './__popup/images-menu__popup';

import './images-menu.scss';

const menuRegistry = new Registry({ id: cnMenu() });

const Menu = compose(withPopupListStyle, withRegistry(menuRegistry))(MenuBase);

export type OnChangeMenuSlug = (slug: string) => void;

interface Props {
    anchorRef: RefObject<HTMLElement>;
    directions?: ImagesPopupDirection[];
    mainOffset?: number;
    scopeRef?: RefObject<HTMLElement>;
    menuItems: ImageMenuItem[];
    onChange?: OnChangeMenuSlug;
    onClose: OnImagesPopupClose;
    animation?: boolean;
    isSmall?: boolean;
    visible: boolean;
    mix?: string;
}

interface Point {
    x: number;
    y: number;
}

interface AnimationOptions {
    active: boolean;
    position: Point;
    url: string;
}

const initAnimationState: AnimationOptions = {
    active: false,
    position: { x: 0, y: 0 },
    url: '',
};

const useAnimation = () => {
    const [animationState, setAnimation] = useState<AnimationOptions>(initAnimationState);

    const startAnimation = (url: string, position: Point) => {
        if (animationState.active) {
            return;
        }

        setAnimation({
            active: true,
            position,
            url,
        });
    };

    const stopAnimation = () => {
        setAnimation((prev) => ({ ...prev, active: false }));
    };

    return { animationState, startAnimation, stopAnimation };
};

export const ImagesMenu = memo((props: Props) => {
    const {
        animation,
        anchorRef,
        directions,
        mainOffset,
        scopeRef,
        menuItems,
        onChange,
        onClose,
        visible,
        isSmall,
        mix,
    } = props;
    const { animationState, startAnimation, stopAnimation } = useAnimation();

    const firstElementMenuRef = useRef<HTMLImageElement>(null);
    const lastElementMenuRef = useRef<HTMLImageElement>(null);
    const imageClickHandler = (url: string, x: number, y: number) => {
        if (animation) {
            startAnimation(url, { x, y });
        }
    };

    const interactWithItem = (currentTarget: EventTarget & HTMLImageElement, menuItem: ImageMenuItem) => {
        const rect = currentTarget.getBoundingClientRect();
        imageClickHandler(menuItem.url, rect.left, rect.top);
    };
    const items = menuItems.map<MixedItem>((menuItem, i) => {
        const refObj: { ref?: React.RefObject<HTMLImageElement> } = {};
        if (i === 0) {
            refObj.ref = firstElementMenuRef;
        }
        if (i === menuItems.length - 1) {
            refObj.ref = lastElementMenuRef;
        }
        return {
            content: (
                <Image
                    aria-label={menuItem.slug}
                    className={b('image-item', { small: isSmall })}
                    onClick={(e) => {
                        interactWithItem(e.currentTarget, menuItem);
                    }}
                    onKeyDown={async (e) => {
                        // зацикливаем переход фокуса в меню
                        // выход по esc
                        if (e.key === 'Tab') {
                            if (i === menuItems.length - 1 && !e.shiftKey) {
                                firstElementMenuRef?.current?.focus?.();
                            }
                            if (e.shiftKey && i === 0) {
                                lastElementMenuRef?.current?.focus?.();
                            }
                        }
                        // работа с клавиатуры
                        if (e.key === 'Enter') {
                            interactWithItem(e.currentTarget, menuItem);
                            onChange?.(menuItem.slug);
                        }
                    }}
                    src={menuItem.url}
                    tabIndex={0}
                    {...refObj}
                />
            ),
            value: menuItem.slug,
        };
    });
    return (
        <ImageContextProvider value={{ resolution: SCREEN_RESOLUTIONS.X1 }}>
            <ImagesPopup
                anchorRef={anchorRef}
                directions={directions}
                mainOffset={mainOffset}
                mix={mix}
                onClose={onClose}
                scopeRef={scopeRef}
                visible={visible}
            >
                <Menu
                    items={items}
                    onChange={(e) => {
                        const slug = e.target.value;
                        if (onChange) {
                            onChange(slug);
                        }
                    }}
                />
            </ImagesPopup>
            {animation && animationState.active && (
                <div
                    className={'images-menu__animation'}
                    style={{
                        left: `${animationState.position.x}px`,
                        top: `${animationState.position.y}px`,
                    }}
                >
                    <Image
                        className={b('animation-image', { small: isSmall })}
                        onAnimationEnd={stopAnimation}
                        src={animationState.url}
                    />
                </div>
            )}
        </ImageContextProvider>
    );
});
